import { Grid, Button, DialogActions, DialogContent, TextField, Typography, Select, MenuItem, FormControl, Stack, FormHelperText } from '@mui/material';
import { IApiIssue, issueService } from '@tap/data-providers';
import { DialogCustom, DialogTitleCustom, FieldWrap, IComponentCss, UseFetchDataState, MdEditor } from '@tap/mui-ui';
import { LoadingButton } from '@mui/lab';
import { ICancelDialogResult, IDialogProps } from '@tap/tap-ui';
import { useFormik } from 'formik';
import * as Yup from 'yup';

/*
*   Диалог: Добавление результата Контроля Качества
*/

type EditQCResultDialogProps = IDialogProps<{
    documentKey: string,
    issueState: UseFetchDataState<IApiIssue>,
    item?: any,
}, ICancelDialogResult>;

const css: IComponentCss = {
    amountInput: {
        mr: 1,
    },
    createButton: {
        mr: 2,
    },
}

export enum DefectTypeCode {
    Unknown = '',
    Fixable = 'Исправимый брак',
    NotFixable = 'Неисправимый брак',
}

export type EditQCResultForm = {
    id?: number,
    defectType: DefectTypeCode,
    defectCategory: string,
    amount: number,
    comment: string,
}


const DefectCategoryList = [
    { code: '020', name: 'Подозрительная шина' },
    { code: '362', name: 'Выходная температура вне пределов' },
    { code: '363', name: 'Внутреннее давление вне пределов' },
    { code: '365', name: 'Внутреннее давление вне пределов' },
    { code: '366', name: 'Внутреннее давление < 7bar' },
    { code: '367', name: 'Перевулканизация шины > 2 мин' },
    { code: '368', name: 'Перевулканизация шины > 3 мин' },
    { code: '376', name: 'PCI – Время цикла вне допуска' },
    { code: '377', name: 'PCI – Давление вне допуска' },
    { code: '378', name: 'PCI – Ширина зажима вне допуска' },
]

export default function EditQCResultDialog({
    data,
    isOpen,
    handleClose,
}: EditQCResultDialogProps) {
    //const commentState = useComment({ documentKeys: [data.documentKey], actionCode: 'add_qc_result' });

    const maxAmount = data.issueState.data.data.amount;

    const { values, touched, errors, ...formik } = useFormik<EditQCResultForm>({
        validateOnChange: true,
        validateOnBlur: true,
        enableReinitialize: true,
        initialValues: {
            id: data.item?.id,
            defectType: data.item ? data.item.defectType : DefectTypeCode.Unknown,
            defectCategory: data.item ? data.item.defectCategory : '',
            amount: data.item ? data.item.amount : 0,
            comment: data.item ? data.item.comment : '',
        },
        validationSchema: Yup.object({
            defectType: Yup.string()
                .required('Укажите тип дефекта.'),
            defectCategory: Yup.string()
                .required('Укажите вид дефекта.'),
            amount: Yup.number()
                .min(1, 'Количество должно быть больше 0')
                .max(maxAmount, `Количество должно быть не больше ${maxAmount}`)
                .required('Заполните количество.'),
        }),

        onSubmit: async (values) => {
            const issue = data.issueState.data || {};
            const defect = {
                ...values,
                defectCategryName: DefectCategoryList.find(d => d.code === values.defectCategory)?.name,
            };
            let qcResultList = [...(issue?.data.qcResultList || [])];

            if (values.id) {
                qcResultList = qcResultList.map((r, id) => {
                    if (id === values.id)
                        return defect;
                    else
                        return r;
                });
            } else {
                qcResultList.push(defect);
            }

            await issueService.updateIssue(data.documentKey, {
                data: {
                    ...issue?.data,
                    qcResultList,
                },
            });


            await data.issueState.fetchData();


            handleClose({ isCancel: false });
        },
    });

    const handleCancel = (): void => {
        handleClose({ isCancel: true });
    }

    return (
        <DialogCustom
            handleClose={handleCancel}
            open={isOpen}
        >

            <DialogTitleCustom
                primaryText={'Результаты контроля'}
            />

            <DialogContent>
                <Grid
                    item
                    container
                    spacing={2}
                    alignItems='stretch'
                >
                    <FieldWrap label={'Тип дефекта'}>

                        <Stack>


                            <Select
                                value={values.defectType}
                                size='small'
                                name='defectType'
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={touched.defectType && Boolean(errors.defectType)}
                                sx={{
                                    maxWidth: '300px',
                                    width: '100%',
                                }}
                            >
                                <MenuItem value={DefectTypeCode.Fixable}>{DefectTypeCode.Fixable}</MenuItem>
                                <MenuItem value={DefectTypeCode.NotFixable}>{DefectTypeCode.NotFixable}</MenuItem>
                            </Select>

                            <FormHelperText error={touched.defectType && Boolean(errors.defectType)}>
                                {errors.defectType}
                            </FormHelperText>

                        </Stack>
                    </FieldWrap>


                    <FieldWrap label={'Вид дефекта'}>
                        <Stack>
                            <Select
                                value={values.defectCategory}
                                size='small'
                                name='defectCategory'
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={touched.defectCategory && Boolean(errors.defectCategory)}
                                sx={{
                                    maxWidth: '300px',
                                    width: '100%',
                                }}
                            >
                                {DefectCategoryList.map(c => (
                                    <MenuItem key={c.code} value={c.code}>{c.name}</MenuItem>
                                ))}
                            </Select>

                            <FormHelperText error={touched.defectCategory && Boolean(errors.defectCategory)}>
                                {errors.defectCategory}
                            </FormHelperText>
                        </Stack>

                    </FieldWrap>


                    <FieldWrap label={'Количество'}>
                        <Grid container alignItems='center'>
                            <TextField
                                sx={css.amountInput}
                                type='number'
                                value={values.amount}
                                size='small'
                                InputProps={{
                                    inputProps: { min: 1, max: 9999 },
                                }}
                                name="amount"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={touched.amount && Boolean(errors.amount)}
                                helperText={touched.amount && errors.amount}
                            />
                            <Typography>{data.issueState.data?.data?.mesureUnit ?? 'шт'}</Typography>
                        </Grid>
                    </FieldWrap>


                    <FieldWrap label={'Комментарий'}>
                        <FormControl sx={{ width: '100%' }}>
                            <MdEditor
                                value={values.comment}
                                onChange={({ html, text }) => formik.setFieldValue('comment', text)}
                            />
                        </FormControl>
                    </FieldWrap>
                </Grid>
            </DialogContent>

            <DialogActions >
                <Grid container justifyContent='flex-end'>

                    <LoadingButton
                        loading={false}
                        onClick={formik.submitForm}
                        //disabled={!formik.isValid}
                        disableElevation
                        type='button'
                        variant='contained'
                        color='primary'
                        sx={css.createButton}
                    >
                        OK
                    </LoadingButton>

                    <Button
                        disableElevation
                        onClick={() => handleCancel()}
                        type='button'
                        variant='text'
                        color='primary'
                    >
                        Отмена
                    </Button>

                </Grid>
            </DialogActions>

        </DialogCustom >
    );
}
